import React, { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../lib/shopApi";

const inputCls = "w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C9A24B] transition-colors";

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const localInputToIso = (v) => (v ? new Date(v).toISOString() : "");

export const SettingsTab = () => {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.get("/settings").then((r) => {
      setForm({
        delivery_flat_rands: (r.data.delivery_flat_cents / 100).toString(),
        delivery_label: r.data.delivery_label || "",
        uber_fee_rands: ((r.data.uber_fee_cents ?? 18000) / 100).toString(),
        uber_label: r.data.uber_label || "",
        collection_available: r.data.collection_available,
        collection_label: r.data.collection_label || "Salon Pick Up — FREE, same day",
        collection_address: r.data.collection_address || "",
        low_stock_threshold: r.data.low_stock_threshold,
        whatsapp: r.data.whatsapp || "",
        shop_announcement: r.data.shop_announcement || "",
        announcement_start: isoToLocalInput(r.data.announcement_start),
        announcement_end: isoToLocalInput(r.data.announcement_end),
      });
    }).catch(() => setForm(null));
  }, []);

  if (!form) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading settings…</p>;

  const set = (key, value) => { setSaved(false); setForm((f) => ({ ...f, [key]: value })); };

  const save = async () => {
    setError("");
    try {
      await adminApi.put("/settings", {
        delivery_flat_cents: Math.round(parseFloat(form.delivery_flat_rands || "0") * 100),
        delivery_label: form.delivery_label,
        uber_fee_cents: Math.round(parseFloat(form.uber_fee_rands || "0") * 100),
        uber_label: form.uber_label,
        collection_available: form.collection_available,
        collection_label: form.collection_label,
        collection_address: form.collection_address,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
        whatsapp: form.whatsapp,
        shop_announcement: form.shop_announcement,
        announcement_start: localInputToIso(form.announcement_start),
        announcement_end: localInputToIso(form.announcement_end),
      });
      setSaved(true);
    } catch (e) {
      setError(formatApiError(e, "Could not save settings"));
    }
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="settings-tab">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Paxi Fee (R)</label>
          <input type="number" min="0" step="0.01" value={form.delivery_flat_rands} onChange={(e) => set("delivery_flat_rands", e.target.value)} className={inputCls} data-testid="settings-delivery-fee" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Uber Fee (R)</label>
          <input type="number" min="0" step="0.01" value={form.uber_fee_rands} onChange={(e) => set("uber_fee_rands", e.target.value)} className={inputCls} data-testid="settings-uber-fee" />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Paxi Label</label>
        <input value={form.delivery_label} onChange={(e) => set("delivery_label", e.target.value)} className={inputCls} data-testid="settings-delivery-label" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Uber Label</label>
        <input value={form.uber_label} onChange={(e) => set("uber_label", e.target.value)} className={inputCls} data-testid="settings-uber-label" />
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.collection_available} onChange={(e) => set("collection_available", e.target.checked)} className="w-5 h-5 accent-[#C9A24B]" data-testid="settings-collection-toggle" />
        <span className="text-sm text-[#F5EFE3]/70">Offer free Salon Pick Up in Johannesburg CBD</span>
      </label>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Pick Up Address</label>
        <input value={form.collection_address} onChange={(e) => set("collection_address", e.target.value)} className={inputCls} data-testid="settings-collection-address" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Low Stock Alert Threshold</label>
        <input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className={inputCls} data-testid="settings-low-stock" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">WhatsApp Number (e.g. 27788583255)</label>
        <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} data-testid="settings-whatsapp" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Announcement Banner (shows at top of homepage &amp; shop — leave blank to hide)</label>
        <input value={form.shop_announcement} onChange={(e) => set("shop_announcement", e.target.value)} placeholder="e.g. Free delivery over R800" className={inputCls} data-testid="settings-announcement" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Banner Starts (optional)</label>
          <input type="datetime-local" value={form.announcement_start} onChange={(e) => set("announcement_start", e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="settings-announcement-start" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Banner Ends (optional)</label>
          <input type="datetime-local" value={form.announcement_end} onChange={(e) => set("announcement_end", e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="settings-announcement-end" />
        </div>
      </div>
      <p className="text-[#F5EFE3]/40 text-xs -mt-3" data-testid="settings-announcement-schedule-hint">Set dates to switch the banner on and off automatically — leave both blank to show it all the time.</p>

      {error && <p className="text-red-400 text-sm" data-testid="settings-error">{error}</p>}
      {saved && <p className="text-green-300 text-sm" data-testid="settings-saved">Settings saved.</p>}

      <button onClick={save} data-testid="settings-save-btn" className="bg-[#C9A24B] text-[#1C1C1C] px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-transform duration-300">
        Save Settings
      </button>
    </div>
  );
};
